// MACHINERY V2 (Stroje) — the fleet CHROME composition (split out of
// FleetPage.tsx at M2.14).
//
// >>> WHY THIS FILE EXISTS. <<< FleetPage stood at 278 of the project's 300
// lines and M2.14's fix pass must ADD to it (the kiosk's forced scope, the
// un-faceted wall set, the `?view=` escape and the Esc exit). M2.13 was granted
// a one-time near-cap exemption at 255 -> 278 on "no good seam"; the ruling
// attached to it was explicit — THE NEXT EDIT SPLITS FIRST, no second
// exemption. This is that split, and it is the sub-phase's first task.
//
// THE SEAM: FleetPage keeps the hooks, the handlers and the page-level
// decisions (which view, which scope, the overlays); this file keeps the CHROME
// the non-kiosk views are wrapped in — the filter rail, the toolbar and the
// scroll area. It is the layer the wall does not have: V4 branches away in
// FleetPage ABOVE this component, which is precisely what makes "kiosk = no
// rail, no toolbar" one branch rather than four conditionals.
//
// IT RETURNS A FRAGMENT, NOT A CONTAINER, ON PURPOSE. The rail/main flex row
// and FleetOverlays must stay SIBLINGS inside the same page-level div — see
// FleetOverlays' "mount depth is the guard" header. Wrapping this in its own
// element would put the overlays one level shallower than the row and quietly
// change what the readiness popup's React ancestry is. The DOM after this split
// is byte-identical to the DOM before it.
//
// STORE READS, NOT PROPS, for `ui` and `query`: both are already-subscribed
// slices and re-reading them here costs one selector, where forwarding them
// would have cost eight props. Nothing that FETCHES lives here — every hook
// that issues a request (and therefore the single telemetry poller) stays in
// FleetPage, which is the invariant its "ONE POLLER PER PAGE" header protects.
//
// ============================================================
//  THE 1280 BRANCH (M2.16a) — AND WHY DESKTOP IS UNTOUCHED
// ============================================================
// Below 1280 the FilterRail DOES NOT RENDER AT ALL — not collapsed, ABSENT
// (mobile/README.md §Overview: "below desktop, the left filter rail
// disappears... the rail never returns until desktop >= ~1280"), and the
// toolbar is replaced by FleetMobileChrome (pattern B + the bottom sheet).
// `railCollapsed` is left alone: it is a desktop preference and squashing it to
// true on a phone rotation would change what the user finds when they rotate
// back.
//
// The desktop path is the SAME TWO ELEMENTS IN THE SAME ORDER it has always
// been — `{isDesktop && <FilterRail/>}` renders nothing extra at >= 1280, and
// the toolbar branch resolves to the identical <FleetToolbar/> — so the DOM at
// desktop width after this change is what it was before it. That is the
// regression guard: a desktop user must not be able to tell this commit
// happened.
//
// The 1280 boundary is `useV2MediaQuery(V2_BELOW_DESKTOP)` — the module's single
// breakpoint vocabulary since finding F-A, and the same string the v2
// stylesheets fence on — never MUI's useMediaQuery and no longer the shared
// integer-width hook. Tablet deliberately sits on the
// MOBILE side of this branch and the DESKTOP side of the nav's
// (MachineryV2Layout keeps the full ChromeBar at 768-1279) — that is what
// screenshots 07-v1-tablet-768.png shows, and the two branches answer different
// questions.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { changeQuery } from "../../customerMachinerySlice";
import {
  MachineryV2FleetView,
  MachineryV2GroupBy,
  setFleetLocationFilter,
  setFleetView,
  toggleFleetStatusFilter,
  toggleRailCollapsed,
} from "../machineryV2UiSlice";
import FilterRail from "./FilterRail";
import FleetContent, { FleetBodyProps } from "./FleetContent";
import FleetMobileChrome from "./FleetMobileChrome";
import FleetToolbar, { FleetScope } from "./FleetToolbar";
import { FleetPageData } from "./fleetPageData";
import { FleetTableSort, FleetTableSortColumn } from "./table/fleetTableSort";

export interface FleetShellProps {
  data: FleetPageData;
  scope: FleetScope;
  onScopeChange: (scope: FleetScope) => void;
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  /** V3 only — held by the page so a V1/V3 round trip keeps the ordering. */
  sort: FleetTableSort;
  onSortChange: (column: FleetTableSortColumn) => void;
  /** Not `setFleetGroupBy` directly: changing the dimension also resets the
   *  collapse set and the table sort, both of which live in the page. */
  onGroupByChange: (groupBy: MachineryV2GroupBy) => void;
  canWrite: boolean;
  onManageGroups: () => void;
  onAddMachine: () => void;
  body: FleetBodyProps;
}

const FleetShell: React.FC<FleetShellProps> = ({
  data,
  scope,
  onScopeChange,
  search,
  onSearchChange,
  onSearchSubmit,
  sort,
  onSortChange,
  onGroupByChange,
  canWrite,
  onManageGroups,
  onAddMachine,
  body,
}) => {
  const dispatch = useAppDispatch();
  const query = useAppSelector((s) => s.customerMachinery.query);
  const ui = useAppSelector((s) => s.machineryV2Ui);
  // F-A: below 1280 is read as the media QUERY both v2 stylesheets fence on,
  // never as an integer width. Identical at every integer width; correct in
  // the fractional band too. See useV2MediaQuery.ts.
  const isDesktop = !useV2MediaQuery(V2_BELOW_DESKTOP);

  return (
    <>
      {isDesktop && (
        <FilterRail
          collapsed={ui.railCollapsed}
          onToggleCollapsed={() => dispatch(toggleRailCollapsed())}
          stateCounts={data.stateCounts}
          selectedStates={ui.fleetStatusFilters}
          onToggleState={(s) => dispatch(toggleFleetStatusFilter(s))}
          types={data.types}
          typeCounts={data.typeCounts}
          selectedTypeId={query.typeId}
          onSelectType={(typeId) =>
            dispatch(changeQuery({ ...query, typeId, manufacturerId: undefined }))
          }
          manufacturers={data.manufacturers}
          selectedManufacturerId={query.manufacturerId}
          onSelectManufacturer={(manufacturerId) =>
            dispatch(changeQuery({ ...query, manufacturerId }))
          }
          locations={data.locations}
          selectedLocation={ui.fleetLocationFilter}
          onSelectLocation={(id) => dispatch(setFleetLocationFilter(id))}
          totalCount={data.flat.length}
        />
      )}

      <main
        style={{
          flex: 1, minWidth: 0, minHeight: 0, display: "flex",
          flexDirection: "column", overflow: "hidden",
        }}
      >
        {isDesktop ? (
          <FleetToolbar
            search={search}
            onSearchChange={onSearchChange}
            onSearchSubmit={onSearchSubmit}
            scope={scope}
            onScopeChange={onScopeChange}
            activeCount={data.activeCount}
            decommissionedCount={data.decommissionedCount}
            view={ui.fleetView}
            onViewChange={(v: MachineryV2FleetView) => dispatch(setFleetView(v))}
            groupBy={ui.fleetGroupBy}
            onGroupByChange={onGroupByChange}
            canWrite={canWrite}
            onManageGroups={onManageGroups}
            onAddMachine={onAddMachine}
          />
        ) : (
          <FleetMobileChrome
            data={data}
            scope={scope}
            onScopeChange={onScopeChange}
            search={search}
            onSearchChange={onSearchChange}
            onSearchSubmit={onSearchSubmit}
            onGroupByChange={onGroupByChange}
            canWrite={canWrite}
            onManageGroups={onManageGroups}
            onAddMachine={onAddMachine}
          />
        )}

        <FleetContent
          data={data}
          scope={scope}
          view={ui.fleetView}
          statusFilters={ui.fleetStatusFilters}
          onToggleStatus={(s) => dispatch(toggleFleetStatusFilter(s))}
          sort={sort}
          onSortChange={onSortChange}
          body={body}
        />
      </main>
    </>
  );
};

export default FleetShell;
